/**
 * Course video upload helper — drives the backend S3 multipart flow:
 *   startCourseUpload -> getCourseUploadUrls -> PUT each 5MB part -> completeCourseUpload
 *
 * The three `*Mutation` args are Apollo mutation functions (from useMutation) that
 * resolve to { data }. Returns the final public { fileUrl }.
 *
 * NOTE: reading each part's ETag requires the S3 bucket CORS to expose the `ETag`
 * header. If completeCourseUpload fails with missing/!etag parts, that CORS config
 * is the likely cause.
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB — must match backend calculatePartCount

// Read a video file's duration (seconds) from its metadata.
export const getVideoDuration = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const seconds = Number.isFinite(video.duration)
        ? Math.round(video.duration)
        : null;
      resolve(seconds);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    video.src = url;
  } catch {
    resolve(null);
  }
});

/**
 * Upload an image (e.g. a video thumbnail) via the single-PUT presigned flow:
 *   uploadCourseImage -> PUT file -> returns the public fileUrl.
 *
 * The backend signs Content-Type into the presigned URL, so the PUT MUST send a
 * matching Content-Type header and the contentType passed to the mutation must
 * equal file.type exactly.
 */
export const uploadCourseImageFile = async ({
  file,
  courseId,
  uploadCourseImage,
}) => {
  if (!file) throw new Error("No image selected");
  if (!file.type?.startsWith("image/")) {
    throw new Error("Only image files are allowed");
  }

  const res = await uploadCourseImage({
    variables: {
      input: {
        courseId,
        fileName: file.name,
        contentType: file.type,
      },
    },
  });
  const payload = res?.data?.uploadCourseImage;
  if (!payload || payload.status !== "success") {
    throw new Error(payload?.message || "Failed to get image upload URL");
  }

  const putRes = await fetch(payload.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error("Failed to upload image");
  }

  return { fileUrl: payload.fileUrl, key: payload.key };
};

export const uploadCourseVideo = async ({
  file,
  startCourseUpload,
  getCourseUploadUrls,
  completeCourseUpload,
  onProgress,
}) => {
  if (!file) throw new Error("No file selected");
  if (!file.type?.startsWith("video/")) {
    throw new Error("Only video files are allowed");
  }

  const partCount = Math.ceil(file.size / CHUNK_SIZE);

  // 1. Start the multipart upload
  const startRes = await startCourseUpload({
    variables: {
      input: {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      },
    },
  });
  const start = startRes?.data?.startCourseUpload;
  if (!start || start.status !== "success") {
    throw new Error(start?.message || "Failed to start upload");
  }
  const { uploadId, key } = start;

  // 2. Get presigned URLs for every part
  const urlsRes = await getCourseUploadUrls({
    variables: {
      input: {
        key,
        uploadId,
        partCount,
        fileSize: file.size,
      },
    },
  });
  const urlsPayload = urlsRes?.data?.getCourseUploadUrls;
  if (!urlsPayload || urlsPayload.status !== "success") {
    throw new Error(urlsPayload?.message || "Failed to get upload URLs");
  }
  const urls = [...(urlsPayload.urls || [])].sort(
    (a, b) => a.partNumber - b.partNumber
  );

  // 3. PUT each chunk and collect its ETag
  const parts = [];
  for (let i = 0; i < urls.length; i += 1) {
    const { partNumber, signedUrl } = urls[i];
    const startByte = (partNumber - 1) * CHUNK_SIZE;
    const chunk = file.slice(startByte, startByte + CHUNK_SIZE);

    // eslint-disable-next-line no-await-in-loop
    const putRes = await fetch(signedUrl, { method: "PUT", body: chunk });
    if (!putRes.ok) {
      throw new Error(`Failed uploading part ${partNumber}`);
    }
    const etag = putRes.headers.get("ETag") || putRes.headers.get("etag");
    if (!etag) {
      throw new Error(
        `Missing ETag for part ${partNumber} — check the S3 bucket CORS ExposeHeaders config`
      );
    }
    parts.push({ etag, partNumber });

    if (onProgress) onProgress(Math.round((parts.length / urls.length) * 100));
  }

  // 4. Complete the upload
  const completeRes = await completeCourseUpload({
    variables: {
      input: { key, uploadId, parts },
    },
  });
  const complete = completeRes?.data?.completeCourseUpload;
  if (!complete || complete.status !== "success") {
    throw new Error(complete?.message || "Failed to complete upload");
  }

  return { fileUrl: complete.fileUrl, key: complete.key };
};
